import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { inputClass } from '../../components/ui/Field';
import { attendanceSummary } from '../../utils/attendance';
import { functionLabel, functionGroups, fullName, memberTypeClass, memberTypeLabel, memberTypeOrder, voiceClass, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, MemberType, Voice } from '../../types';
export function AdminMembers() {
  const {
    members,
    settings
  } = useApp();
  const [iskanje, setIskanje] = useState('');
  const [tip, setTip] = useState<MemberType | 'vsi'>('vsi');
  const [glas, setGlas] = useState<Voice | 'vsi'>('vsi');
  const [funkcija, setFunkcija] = useState<ChoirFunction | 'vse'>('vse');
  const filtrirani = useMemo(() => members.filter(m => {
    if (iskanje && !fullName(m).toLowerCase().includes(iskanje.toLowerCase())) return false;
    if (tip !== 'vsi' && m.type !== tip) return false;
    if (glas !== 'vsi' && m.voice !== glas) return false;
    if (funkcija !== 'vse' && !m.functions.includes(funkcija)) return false;
    return true;
  }), [members, iskanje, tip, glas, funkcija]);
  return <div className="space-y-5" data-id="element-1198" data-mp="29t53ka9zg3e1">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-1199" data-mp="29twyrjzo3z3s">
        <div data-id="element-1200" data-mp="251vfhe431849">
          <h1 className="text-2xl font-semibold text-ink" data-id="element-1201" data-mp="251dmqrgmpc13">Člani</h1>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1202" data-mp="2043ocmb9hq14">
            {members.length} vpisanih oseb · {members.filter(m => m.type === 'aktivni').length} aktivnih
          </p>
        </div>
      </div>

      <Card className="p-4" data-id="element-1203" data-mp="2srpqdq4ucmlv">
        <div className="grid gap-3 md:grid-cols-4" data-id="element-1204" data-mp="2srhg8603rved">
          <div className="relative md:col-span-1" data-id="element-1205" data-mp="3born1u2e60bv">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" data-id="element-1206" data-mp="3bp1frpetmll9" />
            <input value={iskanje} onChange={e => setIskanje(e.target.value)} placeholder="Išči po imenu…" aria-label="Išči člane" className={`${inputClass} pl-9`} data-id="element-1207" data-mp="3gpp1bvka6a83" />
          </div>
          <select value={tip} onChange={e => setTip(e.target.value as MemberType | 'vsi')} aria-label="Tip članstva" className={inputClass} data-id="element-1208" data-mp="2sx8lzsrxgwch">
            <option value="vsi" data-id="element-1209" data-mp="2jyn47zua5lcr">Vsi tipi članstva</option>
            {memberTypeOrder.map((t: MemberType) => <option key={t} value={t} data-id="element-1210" data-mp="2nz73b8yeyz1j">
                {memberTypeLabel[t]}
              </option>)}
          </select>
          <select value={glas} onChange={e => setGlas(e.target.value as Voice | 'vsi')} aria-label="Glasovna skupina" className={inputClass} data-id="element-1211" data-mp="m7k62pnxi7wm">
            <option value="vsi" data-id="element-1212" data-mp="22o9i7djkmdw1">Vse glasovne skupine</option>
            {voiceOrder.map(v => <option key={v} value={v} data-id="element-1213" data-mp="2gf4iw2jx1ooj">
                {voiceLabel[v]}
              </option>)}
          </select>
          <select value={funkcija} onChange={e => setFunkcija(e.target.value as ChoirFunction | 'vse')} aria-label="Funkcija" className={inputClass} data-id="element-1214" data-mp="2bnmtmbr99cje">
            <option value="vse" data-id="element-1215" data-mp="2ldpiq0v9a6cg">Vse funkcije</option>
            {functionGroups.map(g => <optgroup key={g.label} label={g.label} data-id="element-1216" data-mp="2zcz033v776i0">
                {g.funkcije.map(f => <option key={f} value={f} data-id="element-1217" data-mp="2uggyudz2tqaf">
                    {functionLabel[f]}
                  </option>)}
              </optgroup>)}
          </select>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-1218" data-mp="102pfgmqy3s1s">
        <div className="overflow-x-auto viva-scroll" data-id="element-1219" data-mp="12ft9wk7p7nl7">
          <table className="w-full min-w-[900px] text-left text-sm" data-id="element-1220" data-mp="12f9sd4lyn17p">
            <thead className="border-b border-line bg-canvas text-[12px] uppercase tracking-wide text-ink-muted" data-id="element-1221" data-mp="16gmzrj4pbcgq">
              <tr data-id="element-1222" data-mp="1bhiylia748fa">
                <th className="px-5 py-3 font-medium" data-id="element-1223" data-mp="1bh100f029i47">Član</th>
                <th className="px-4 py-3 font-medium" data-id="element-1224" data-mp="1gfub60rvkx17">Tip članstva</th>
                <th className="px-4 py-3 font-medium" data-id="element-1225" data-mp="iru13ewp2qif">Glas</th>
                <th className="px-4 py-3 font-medium" data-id="element-1226" data-mp="itercsbz7nah">Funkcije</th>
                <th className="px-4 py-3 font-medium" data-id="element-1227" data-mp="nq5bprutd51p">Prisotnost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1228" data-mp="soymhcipuhy6">
              {filtrirani.map(m => {
              const s = attendanceSummary(m, settings);
              return <tr key={m.id} className="transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1229" data-mp="1ufx33z5vka">
                    <td className="px-5 py-3" data-id="element-1230" data-mp="4zvzrq985ywt">
                      <Link to={`/admin/clani/${m.id}`} className="flex items-center gap-3" data-id="element-1231" data-mp="4ykvzqfs4r5p">
                        <Avatar member={m} size="sm" data-id="element-1232" data-mp="3tliue0cd7yr8" />
                        <span data-id="element-1233" data-mp="3omnz3yx7ohq8">
                          <span className="block font-medium text-ink" data-id="element-1234" data-mp="3onqscdi0aw9b">{fullName(m)}</span>
                          <span className="block text-[12px] text-ink-muted" data-id="element-1235" data-mp="3jmtay34tm68v">{m.email}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3" data-id="element-1236" data-mp="3any1mi6hcg1c">
                      <Badge className={memberTypeClass[m.type]} data-id="element-1237" data-mp="3aor6fdlvovgu">{memberTypeLabel[m.type]}</Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-1238" data-mp="30xmoc50wouhs">
                      <Badge className={voiceClass[m.voice]} data-id="element-1239" data-mp="30yelhka5j0a6">{voiceLabel[m.voice]}</Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-1240" data-mp="2w03dlf3f2zul">
                      <span className="flex flex-wrap gap-1.5" data-id="element-1241" data-mp="2qz77cuvlf1m0">
                        {m.functions.map(f => <Badge key={f} data-id="element-1242" data-mp="2mxlngpkn5n2b">{functionLabel[f]}</Badge>)}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1243" data-mp="2cbfca7t2pxtd">
                      {m.type === 'aktivni' ? <StatusBadge status={s.status} odstotek={s.odstotek} data-id="element-1244" data-mp="2hd4npok7kdhz" /> : <span className="text-[13px] text-ink-muted" data-id="element-1245" data-mp="23e4zuj4is2mv">—</span>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        {filtrirani.length === 0 && <p className="px-5 py-10 text-center text-sm text-ink-muted" data-id="element-1246" data-mp="30118cme5fwx5">Ni članov, ki bi ustrezali filtrom.</p>}
      </Card>
    </div>;
}